import { useEffect, useMemo } from "react";
import { Flame, Play, Sprout } from "lucide-react";
import { FAMILIES, type Family } from "../../shared/model";
import { LinkButton } from "../ui/kit";
import { Ring } from "../ui/kit";
import { Shell } from "../ui/Shell";
import { PuzzleCard } from "../ui/PuzzleCard";
import { useSession } from "../app/session";
import * as store from "../app/store";
import { useStore } from "../app/store";
import { dayKey, greeting, longDate, parseDay } from "../app/clock";
import { dayTotals, streak } from "../app/stats";
import { proposeFor } from "../puzzles/catalog";

/** short, original reminders about how to think; one per day */
const TIPS = [
  "Being stuck is the workout, not a sign that you're failing.",
  "Draw it. Even a bad sketch gives your thinking somewhere to live.",
  "Try the smallest version of the problem first.",
  "Say what you know for sure, then what you are only assuming.",
  "If an idea keeps failing, ask what it is trying to tell you.",
  "A wrong guess that teaches you something is progress.",
  "Look for what stays the same while everything else changes.",
  "Work backwards: picture the last step, then the one before it.",
  "When you are stuck, change what you are looking at: a corner, an edge, one piece.",
  "Explain it out loud to an imaginary friend. The gaps show up on their own.",
  "Ten regular minutes beat one heroic hour.",
  "There is often more than one right answer. Can you find another?",
  "Sleep on it. Your brain keeps working when you stop.",
  "Slow is fine. Thinking well is not a race."
];

export default function Today() {
  const { user } = useSession();
  const day = dayKey();
  const name = useStore((d) => d.name);
  const assign = useStore((d) => d.assign[day]);
  const progress = useStore((d) => d.progress);
  const secs = useStore((d) => dayTotals(d, day).total);
  const goalMin = useStore((d) => d.settings.goalMinutes);
  const st = useStore((d) => streak(d, day));
  const owner = useStore((d) => d.owner);

  // fix today's three the first time they are needed, so they stay put all day
  useEffect(() => {
    const d = store.get();
    const cur = d.assign[day] ?? {};
    const missing: Partial<Record<Family, string>> = {};
    for (const f of FAMILIES) if (!cur[f]) missing[f] = proposeFor(f, d, day);
    if (Object.keys(missing).length) store.setAssignment(day, missing);
  }, [day, progress]);

  const ids = FAMILIES.map((f) => assign?.[f]).filter((x): x is string => !!x);
  const open = ids.filter((id) => !["solved", "revealed"].includes(progress[id]?.status ?? ""));
  const doneCount = ids.length - open.length;
  const goal = goalMin * 60;
  const met = secs >= goal;
  const more = useMemo(() => FAMILIES.map((f) => proposeFor(f, store.get(), day)).filter((id) => !ids.includes(id)), [ids.join(","), progress]); // eslint-disable-line react-hooks/exhaustive-deps
  const first = (name || user?.name || "").split(" ")[0];
  const started = secs > 0 || doneCount > 0;

  return (
    <Shell>
      <div className="wrap today">
        <section className="today-hero card">
          <div className="today-hero-copy">
            <p className="kicker">{longDate(day)}</p>
            <h1>{greeting()}{first ? `, ${first}` : ""}.</h1>
            <p className="lede">
              {met
                ? "That's today's thinking done. Everything from here is a bonus."
                : doneCount === ids.length && ids.length > 0
                  ? "Today's three are solved. A little more thinking to reach your goal?"
                  : "Ten quiet minutes for your own brain: three puzzles, no AI, no rush."}
            </p>
            <p className="hand tip">Pebble's tip: {TIPS[Math.floor(parseDay(day).getTime() / 86400000) % TIPS.length]}</p>
            <div className="row-gap" style={{ marginTop: 18 }}>
              {open.length > 0 ? (
                <LinkButton to={"/p/" + open[0]} kind="primary" size="big"><Play size={18} /> {started ? "Continue today's session" : "Start today's session"}</LinkButton>
              ) : (
                <LinkButton to="/puzzles" kind="pond" size="big">Choose another puzzle</LinkButton>
              )}
              {!user && owner === "guest" && <LinkButton to="/signup" size="big">Save my progress</LinkButton>}
            </div>
          </div>
          <div className="today-hero-ring">
            <Ring value={secs} max={goal} size={168} stroke={13} done={met}>
              <div className="ring-big">{Math.floor(secs / 60)}<small>/{goalMin} min</small></div>
              <div className="ring-sub">{met ? "goal reached" : "thinking today"}</div>
            </Ring>
            <div className="streak-row">
              <span className="streak" title="Days in a row that you reached your goal"><Flame size={17} /> {st.current} day{st.current === 1 ? "" : "s"} in a row</span>
              {st.best > st.current && <span className="muted small">best {st.best}</span>}
            </div>
          </div>
        </section>

        {met && ids.length > 0 && open.length === 0 && (
          <section className="rest-card card">
            <img src="/art/rest.webp" alt="Pebble asleep on a lily pad" width="360" height="240" loading="lazy" />
            <div>
              <h2>Rest well.</h2>
              <p className="muted">Your brain grows while you rest, too. Come back tomorrow for three new puzzles, or play more below if you're enjoying yourself.</p>
            </div>
          </section>
        )}

        <div className="section-head">
          <h2>Today's three</h2>
          <span className="muted small">{doneCount} of {ids.length || 3} done</span>
        </div>
        <div className="card-grid">
          {ids.length === 0 ? <div className="muted">Getting your puzzles ready…</div> : ids.map((id) => <PuzzleCard key={id} id={id} />)}
        </div>

        {open.length === 0 && more.length > 0 && (
          <>
            <div className="section-head">
              <h2>Want more?</h2>
              <span className="muted small">Next up in each family</span>
            </div>
            <div className="card-grid">
              {more.map((id) => <PuzzleCard key={id} id={id} />)}
            </div>
          </>
        )}

        {!user && owner === "guest" && (
          <aside className="guest-note card">
            <Sprout size={22} />
            <div>
              <strong>Your progress is saved on this device.</strong>
              <p className="muted small">A free account keeps it safe and syncs it between your phone, tablet and computer. No e-mails, no ads.</p>
            </div>
            <LinkButton to="/signup" kind="pond" size="small">Create free account</LinkButton>
          </aside>
        )}
      </div>
    </Shell>
  );
}
